import {
  mapWishlistItemToProductInfo,
  findWishlistItem,
  mapStorefrontProduct,
} from "./StateUtil";
import { CacheStrategy } from "../api/CacheApi";
import { Observable } from "./Observable";
import { createDebug } from "../utils/LazyDebug";

import type {
  ApiWishlistItemRef,
  Wishlist,
  WishlistApi,
} from "../api/WishlistApi";
import type { ShopifyApi, ShopifyCart } from "../api/ShopifyApi";
import type { WishlistSettings, WishlistUtils } from "../WishlistApp";
import type {
  WishlistEvents,
  WishlistEvent,
  WishlistEventName,
} from "../utils/WishlistEvents";
import type { StorefrontApi } from "../api/StorefrontApi";

export interface WishlistStateParams {
  wishlistApi: WishlistApi;
  shopifyApi: ShopifyApi;
  storefrontApi: StorefrontApi;
  settings: WishlistSettings;
  events: WishlistEvents;
  utils: WishlistUtils;
}

const debug = createDebug("wk:state");

export class WishlistState {
  #wishlistApi: WishlistApi;
  #shopifyApi: ShopifyApi;
  #storefrontApi: StorefrontApi;
  #events: WishlistEvents;
  #settings: WishlistSettings;
  #utils: WishlistUtils;
  #pendingAction?: WishlistStateAction;

  constructor({
    wishlistApi,
    shopifyApi,
    storefrontApi,
    settings,
    events,
    utils,
  }: WishlistStateParams) {
    this.#wishlistApi = wishlistApi;
    this.#shopifyApi = shopifyApi;
    this.#storefrontApi = storefrontApi;
    this.#settings = settings;
    this.#events = events;
    this.#utils = utils;
  }

  async revalidate(force = false) {
    debug("Revalidate wishlist state");
    await this.clearCache(force);
    this.#events.publish({
      name: "state:refetch",
    });
  }

  #publishStateAction(action: WishlistStateAction) {
    this.#pendingAction = action;
    this.#events.publish({
      name: "state:action",
      data: {
        stateAction: action,
      },
    });
  }

  observeWishlist({ wishlistId }: { wishlistId: string }) {
    return new Observable<Wishlist>((subscriber) => {
      let previousState: Wishlist = {
        id: wishlistId,
        isMine: wishlistId === "mine",
        items: [],
        numItems: 0,
      };

      const refetch = () => {
        // debug("Refetch wishlist", { wishlistId });
        this.#wishlistApi.loadWishlist({ wishlistId }).then(({ wishlist }) => {
          if (JSON.stringify(wishlist) !== JSON.stringify(previousState)) {
            previousState = wishlist;

            debug("Update wishlist state", {
              wishlistId,
              nextState: wishlist,
            });

            subscriber.next(wishlist);
          }

          // TODO: This should be removed from here
          // Wait until refetch complete before unblocking
          this.#pendingAction = undefined;
        });
      };

      const update = (event: WishlistEvent) => {
        if (!previousState || !event.data?.stateAction) return;

        const optimisticState = this.#applyOptimisticUpdate(
          previousState,
          event.data.stateAction,
        );

        debug("Update wishlist state (optimistic)", {
          wishlistId,
          optimisticState,
        });
        subscriber.next(optimisticState);
      };

      this.#events.subscribe("state:refetch", refetch);
      this.#events.subscribe("state:action", update);

      // debug("Fetch initial wishlist");
      // subscriber.next(previousState);
      refetch();
    });
  }

  observeProductInfo({
    productHandle,
    variantId,
  }: {
    productHandle: string;
    variantId?: number;
  }) {
    return new Observable<ProductInfo>((subscriber) => {
      let previousState: ProductInfo;

      this.observeWishlist({ wishlistId: "mine" }).subscribe((wishlist) => {
        const wishlistItem = findWishlistItem({
          wishlist,
          productHandle,
          variantId,
          mulitVariantMode: this.#settings.general.wishlistMode === "VARIANT",
        });

        const productInfo = mapWishlistItemToProductInfo({
          wishlistItem,
          productHandle,
          variantId,
        });

        if (JSON.stringify(productInfo) !== JSON.stringify(previousState)) {
          previousState = productInfo;
          subscriber.next(productInfo);
        }
      });
    });
  }

  observeShopifyCart({ cacheStrategy }: { cacheStrategy: CacheStrategy }) {
    let previousState: ShopifyCart = {
      token: "",
      total_price: 0,
      total_weight: 0,
      item_count: 0,
      items: [],
      requires_shipping: true,
      currency: "USD",
      items_subtotal_price: 0,
    };

    return new Observable<ShopifyCart>((subscriber) => {
      const refetch = () => {
        debug("Refetch cart");
        this.#shopifyApi.loadCart({ cacheStrategy }).then((cart) => {
          if (JSON.stringify(cart) !== JSON.stringify(previousState)) {
            previousState = cart;

            debug("Update cart state (remote)", {
              nextState: cart,
            });

            subscriber.next(cart);
          }
        });
      };

      this.#events.subscribe("state:refetch", refetch);

      refetch();
    });
  }

  async addWishlistItem(params: {
    productHandle: string;
    variantId?: number | string;
  }) {
    if (this.#pendingAction) return;

    debug("Add wishlist item", { params });

    const variantId =
      typeof params.variantId === "string"
        ? parseInt(params.variantId)
        : params.variantId;

    const productHandle = params.productHandle;

    if (!productHandle) {
      throw new Error("Product handle is required");
    }

    this.#throwIfLoginRequired("wishlist:add", {
      productHandle,
      variantId,
    });

    this.#publishStateAction({
      name: "wishlist:add",
      state: "submitting",
      body: {
        productHandle,
        variantId,
      },
    });

    const productId = await this.#storefrontApi.loadProductIdByHandle({
      productHandle,
    });
    const { wishlistItem, sessionId } = await this.#wishlistApi
      .addItem({
        productId,
        variantId,
      })
      .catch((error) => {
        console.error(error);
        this.revalidate();
        throw error;
        // this.#events.publish({
        //   name: "wishlist:add:error",
        //   error,
        // });
      });

    if (sessionId) {
      this.#events.publish({
        name: "app:init-sid",
        data: {
          sessionId,
        },
      });
    }

    debug("Added wishlist item", { params, wishlistItem });

    this.revalidate();
    this.publishEvent({
      eventName: "wishlist:add:success",
      wishlistId: "mine",
      productId,
      variantId,
    });

    return { wishlistItem };
  }

  async saveForLater(params: {
    productHandle: string;
    variantId?: number | string;
  }) {
    if (this.#pendingAction) return;

    const variantId =
      typeof params.variantId === "string"
        ? parseInt(params.variantId)
        : params.variantId;

    const productHandle = params.productHandle;

    debug("Save for later", { params });

    if (!productHandle) {
      throw new Error("Product handle is required");
    }

    if (!variantId) {
      throw new Error("Variant ID is required");
    }

    this.#throwIfLoginRequired("wishlist:save-for-later", {
      productHandle,
      variantId,
    });

    const [productId, { wishlist }] = await Promise.all([
      this.#storefrontApi.loadProductIdByHandle({
        productHandle,
      }),
      this.#wishlistApi.loadWishlist({
        wishlistId: "mine",
      }),
    ]);

    const wishlistItem = findWishlistItem({
      wishlist,
      productHandle,
      variantId,
      mulitVariantMode: true,
    });

    let newWishlistItem: ApiWishlistItemRef | undefined;
    if (!wishlistItem?.variantId) {
      newWishlistItem = await this.addWishlistItem(params).then(
        (result) => result?.wishlistItem,
      );
    }

    if (!wishlistItem?.id && !newWishlistItem?.id) {
      throw new Error("Could not save for later");
    }

    debug("Saved for later", { params, wishlistItem });

    this.publishEvent({
      eventName: "wishlist:save-for-later:success",
      wishlistId: "mine",
      productId,
      variantId,
    });

    return {
      id: wishlistItem?.id ?? newWishlistItem?.id!,
      productId,
      variantId,
    };
  }

  async updateWishlistItem({
    wishlistItemId,
    changes,
  }: {
    wishlistItemId: string;
    changes: {
      variantId: number | string;
    };
  }) {
    if (this.#pendingAction) return;

    debug("Update wishlist item", { wishlistItemId, changes });

    const variantId =
      typeof changes.variantId === "string"
        ? parseInt(changes.variantId)
        : changes.variantId;

    this.#throwIfLoginRequired("wishlist:change-variant", {
      wishlistItemId,
      variantId,
    });

    this.#publishStateAction({
      name: "wishlist:change-variant",
      state: "submitting",
      body: {
        wishlistItemId,
        variantId,
      },
    });

    const { wishlistItem } = await this.#wishlistApi
      .updateItem({
        wishlistItemId,
        changes: { variantId },
      })
      .catch((error) => {
        console.error(error);
        this.revalidate();
        throw error;
        // this.#events.publish({
        //   name: "wishlist:change-variant:error",
        //   error,
        // });
      });

    debug("Updated wishlist item", { wishlistItemId, changes, wishlistItem });

    this.revalidate();

    this.publishEvent({
      eventName: "wishlist:change-variant:success",
      wishlistId: "mine",
      productId: wishlistItem.product.id,
      variantId,
    });

    return { wishlistItem };
  }

  async removeWishlistItem({ wishlistItemId }: { wishlistItemId: string }) {
    if (this.#pendingAction) return;

    debug("Remove wishlist item", { wishlistItemId });

    this.#throwIfLoginRequired("wishlist:remove", {
      wishlistItemId,
    });

    this.#publishStateAction({
      name: "wishlist:remove",
      state: "submitting",
      body: {
        wishlistItemId,
      },
    });

    const { wishlistItem } = await this.#wishlistApi
      .removeItem({
        wishlistId: "mine",
        wishlistItemId,
      })
      .catch((error) => {
        console.error(error);
        this.revalidate();
        throw error;
        // this.#events.publish({
        //   name: "wishlist:remove:error",
        //   error,
        // });
      });

    debug("Removed wishlist item", { wishlistItemId, wishlistItem });

    this.revalidate();

    this.publishEvent({
      eventName: "wishlist:remove:success",
      wishlistId: "mine",
      productId: wishlistItem.product.id,
      variantId: wishlistItem.selectedVariantId,
    });

    return { wishlistItem };
  }

  async clearWishlist() {
    if (this.#pendingAction) return;

    debug("Clear wishlist");

    this.#throwIfLoginRequired("wishlist:clear");

    this.#publishStateAction({
      name: "wishlist:clear",
      state: "submitting",
    });

    await this.#wishlistApi.clearWishlist().catch((error) => {
      console.error(error);
      this.revalidate();
      throw error;
      // this.#events.publish({
      //   name: "wishlist:clear:error",
      //   error,
      // });
    });

    debug("Cleared wishlist");

    this.revalidate();
    this.publishEvent({
      eventName: "wishlist:clear:success",
      wishlistId: "mine",
    });
  }

  async clearCache(force = false) {
    debug("Clear cache");
    await this.#shopifyApi.clearCache(force);
    await this.#wishlistApi.clearCache(force);
  }

  async addToCart({
    wishlistId,
    wishlistItemId,
    variantId,
    quantity = 1,
    properties,
    sectionsToRender,
    sectionsUrl,
  }: {
    wishlistId: string;
    wishlistItemId: string;
    variantId: number;
    quantity?: number;
    properties?: Record<string, string>;
    sectionsToRender?: string[];
    sectionsUrl?: string;
  }) {
    // if (this.#pendingAction) return;

    if (wishlistId === "mine") {
      this.#throwIfLoginRequired("wishlist:add-to-cart");
    }

    const cartResponse = await this.#shopifyApi.addToCart({
      id: variantId,
      quantity,
      properties,
      sections: sectionsToRender,
      sections_url: sectionsUrl ? encodeURI(sectionsUrl) : undefined,
    });

    const [{ wishlist }, cart] = await Promise.all([
      this.#wishlistApi.loadWishlist({
        wishlistId,
        cacheStrategy: CacheStrategy.None,
      }),
      this.#shopifyApi.loadCart({ cacheStrategy: CacheStrategy.None }),
    ]);

    const wishlistItem = wishlist.items.find(
      (item) => item.id === wishlistItemId,
    )!;

    this.revalidate();
    this.publishEvent({
      eventName: "wishlist:add-to-cart:success",
      wishlistId,
      productId: wishlistItem.product.id,
      variantId,
    });

    return {
      wishlist,
      wishlistItem,
      cart,
      cartResponse,
    };
  }

  // DEPRECATED (check add-wishlist-to-cart.js)
  async addAllToCart({
    wishlistId,
    sectionsToRender,
    sectionsUrl,
  }: {
    wishlistId: string;
    sectionsToRender?: string[];
    sectionsUrl?: string;
  }) {
    if (this.#pendingAction) return;

    const isMine = wishlistId === "mine";

    if (isMine) {
      this.#throwIfLoginRequired("wishlist:add-to-cart");
    }

    const { wishlist } = await this.#wishlistApi.loadWishlist({
      wishlistId,
      cacheStrategy: CacheStrategy.None,
    });

    const items = wishlist.items
      .filter((item) => !!item.variantId)
      .filter((item) => {
        const variant = item.product.variants.find(
          (variant) => variant.id === item.variantId,
        );
        return !!variant?.available;
      });

    if (!items.length) {
      return { cartResponse: null, wishlistItems: [] };
    }

    const cartResponse = await this.#shopifyApi.addToCart({
      items:
        items.map((item) => ({
          id: item.variantId!,
          quantity: 1,
        })) ?? [],
      sections: sectionsToRender,
      sections_url: sectionsUrl ? encodeURI(sectionsUrl) : undefined,
    });

    this.revalidate();
    this.publishEvent({
      eventName: "wishlist:add-all-to-cart:success",
      wishlistId: "mine",
    });

    return { cartResponse, wishlistItems: items };
  }

  async shareWishlist({
    wishlistId,
    title,
    text,
  }: {
    wishlistId: string;
    title: string;
    text: string;
  }) {
    if (this.#pendingAction) return;

    if (wishlistId === "mine") {
      this.#throwIfLoginRequired("wishlist:share");
    }

    const shareData = {
      url: this.#utils.url.getPublicWishlistUrl(wishlistId),
      title,
      text,
    };

    const result = { webShare: false, clipboard: false };

    try {
      if (/Mobile|Android|BlackBerry/i.test(navigator.userAgent)) {
        await navigator.share(shareData);
        result.webShare = true;
      }
    } catch (error) {
      result.webShare = false;
    }

    if (!result.webShare) {
      try {
        await navigator.clipboard.writeText(shareData.url);
        result.clipboard = true;
      } catch (error) {
        result.clipboard = false;
      }
    }

    this.publishEvent({
      eventName: "wishlist:share:success",
      wishlistId,
    });

    return result;
  }

  async publishEvent({
    eventName,
    wishlistId,
    productId,
    variantId,
  }: {
    eventName: WishlistEventName;
    wishlistId: string;
    productId?: number | null;
    variantId?: number | null;
  }) {
    const [wishlist, product] = await Promise.all([
      wishlistId
        ? this.#wishlistApi
            .loadWishlist({ wishlistId })
            .then(({ wishlist }) => wishlist)
        : Promise.resolve(undefined),
      productId
        ? this.#storefrontApi
            .loadProduct({
              productId,
            })
            .then((product) => (product ? mapStorefrontProduct(product) : null))
        : Promise.resolve(undefined),
    ]);

    const variant = variantId
      ? product?.variants.find((variant) => variant.id === variantId)
      : undefined;

    this.#events.publish({
      name: eventName,
      data: {
        wishlist,
        product,
        variant,
        // quantity: 1,
      },
    });
  }

  #applyOptimisticUpdate(
    wishlist: Wishlist,
    action: WishlistStateAction,
  ): Wishlist {
    switch (action.name) {
      case "wishlist:add":
        return {
          ...wishlist,
          items: [
            {
              id: `temp-${Date.now()}`,
              variantId: action.body?.variantId ?? 0,
              product: {
                id: action.body?.productId ?? 0,
                handle: action.body?.productHandle ?? "",
                variants: [],
              },
            },
            ...wishlist.items,
          ],
          numItems: wishlist.items.length + 1,
        };
      case "wishlist:remove":
        return {
          ...wishlist,
          items: wishlist.items.filter(
            (item) => item.id !== action.body?.wishlistItemId,
          ),
          numItems: wishlist.items.length - 1,
        };
      default:
        return wishlist;
    }
  }

  #maybeRequireLogin(
    action: WishlistActionName,
    params?: {
      wishlistItemId?: string;
      productHandle?: string;
      variantId?: number;
    },
  ) {
    if (
      this.#settings.general.wishlistAccessMode === "UNRESTRICTED" ||
      this.#wishlistApi.hasCustomer
    ) {
      return false;
    }

    const actionRequiresLogin =
      loginActions[this.#settings.general.wishlistAccessMode]?.includes(action);

    if (actionRequiresLogin) {
      const ignoreActions = ["wishlist:load"];
      if (!ignoreActions.includes(action)) {
        this.#events.publish({
          name: "wishlist:login-required",
          data: {
            action,
            ...params,
          },
        });
      }
      return true;
    }
  }

  #throwIfLoginRequired(
    action: WishlistActionName,
    params?: {
      wishlistItemId?: string;
      productHandle?: string;
      variantId?: number;
    },
  ) {
    if (this.#maybeRequireLogin(action, params)) {
      throw new Error("Wishlist requires login");
    }
  }
}

const loginActions = {
  REQUIRE_LOGIN_BEFORE_USE: [
    "wishlist:load",
    "wishlist:add",
    "wishlist:remove",
    "wishlist:change-variant",
    "wishlist:clear",
    "wishlist:add-to-cart",
    "wishlist:share",
    "wishlist:view",
    "wishlist:save-for-later",
  ],
  REQUIRE_LOGIN_TO_VIEW_LIST: ["wishlist:view"],
};

export interface ProductInfo {
  productHandle: string;
  productId: number;
  variantId?: number;
  inWishlist: boolean;
  wishlistItemId?: string;
}

export interface WishlistStateAction {
  name: WishlistActionName;
  state: "submitting" | "loading" | "idle";
  body?: {
    productHandle?: string;
    productId?: number;
    variantId?: number;
    wishlistItemId?: string;
  };
}

export type WishlistActionName =
  | "wishlist:load"
  | "wishlist:add"
  | "wishlist:remove"
  | "wishlist:change-variant"
  | "wishlist:clear"
  | "wishlist:add-to-cart"
  | "wishlist:share"
  | "wishlist:save-for-later"
  | "wishlist:view";
